import { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Scale, LogOut, Droplets, FileDown, RefreshCw, Package, Layers, Bell, AlertTriangle, X, Calendar, Truck, ArrowRightLeft, Edit, MoveRight, Save, Hash, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper per ottenere data/ora italiana
const getItalianDateTime = () => {
  const now = new Date();
  const italianTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
  const year = italianTime.getFullYear();
  const month = String(italianTime.getMonth() + 1).padStart(2, '0');
  const day = String(italianTime.getDate()).padStart(2, '0');
  const hours = String(italianTime.getHours()).padStart(2, '0');
  const minutes = String(italianTime.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [nextRefresh, setNextRefresh] = useState(120);
  const [richiesteCount, setRichiesteCount] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [cellaStats, setCellaStats] = useState({ bins_attivi: 0, kg_totale: 0, bins: [] });
  const [selectedBin, setSelectedBin] = useState(null);
  const [binMovimenti, setBinMovimenti] = useState([]);
  const [expandedId, setExpandedId] = useState(null); // ID espanso per vedere i bins
  
  // Stati per le azioni del modal
  const [modalAction, setModalAction] = useState(null); // 'modifica', 'uscita', 'spostamento', 'unione'
  const [actionLoading, setActionLoading] = useState(false);
  const [fornitori, setFornitori] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [binsStessoLotto, setBinsStessoLotto] = useState([]); // Per unione
  
  // Form data per modifica
  const [editData, setEditData] = useState({});
  
  // Form data per uscita
  const [uscitaData, setUscitaData] = useState({
    kg_uscita: '',
    destinazione: '',
    data_ora_uscita: getItalianDateTime()
  });
  
  // Form data per spostamento
  const [spostamentoData, setSpostamentoData] = useState({
    kg_uscita: '',
    nuovo_bin_number: '',
    nuova_doccia_number: '',
    data_ora_uscita: getItalianDateTime()
  });

  // Form data per unione
  const [unioneData, setUnioneData] = useState({
    bin_destinazione: '',
    kg_da_trasferire: '',
    data_operazione: getItalianDateTime()
  });

  useEffect(() => {
    fetchData();
    fetchRichieste();
    fetchCellaStats();
    fetchFornitori();
    fetchProdotti();
    const interval = setInterval(fetchData, 120000);
    const richiesteInterval = setInterval(fetchRichieste, 30000); // Check ogni 30 sec
    const cellaInterval = setInterval(fetchCellaStats, 60000); // Check cella ogni 60 sec
    const countdownInterval = setInterval(() => {
      setNextRefresh(prev => prev > 0 ? prev - 1 : 120);
    }, 1000);
    
    return () => {
      clearInterval(interval);
      clearInterval(richiesteInterval);
      clearInterval(cellaInterval);
      clearInterval(countdownInterval);
    };
  }, []);

  const fetchFornitori = async () => {
    try {
      const response = await axios.get(`${API}/fornitori`);
      setFornitori(response.data);
    } catch (error) {
      console.error('Error fetching fornitori:', error);
    }
  };

  const fetchProdotti = async () => {
    try {
      const response = await axios.get(`${API}/prodotti`);
      setProdotti(response.data);
    } catch (error) {
      console.error('Error fetching prodotti:', error);
    }
  };

  const fetchCellaStats = async () => {
    try {
      const response = await axios.get(`${API}/cella/stats`);
      setCellaStats(response.data);
    } catch (error) {
      console.error('Error fetching cella stats:', error);
    }
  };

  const fetchRichieste = async () => {
    try {
      const response = await axios.get(`${API}/richieste-importazione`);
      // L'API già filtra per status=pending, quindi contiamo tutti
      const pendingCount = response.data.length;
      
      // Mostra notifica se ci sono nuove richieste
      if (pendingCount > richiesteCount && richiesteCount > 0) {
        toast.info(`Nuova richiesta importazione! (${pendingCount} in attesa)`, {
          icon: <Bell className="w-4 h-4 text-[#FFE500]" />,
          duration: 5000
        });
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 3000);
      }
      
      setRichiesteCount(pendingCount);
    } catch (error) {
      console.error('Error fetching richieste:', error);
    }
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
      const now = new Date();
      setLastUpdate(now.toLocaleTimeString('it-IT', { timeZone: 'Europe/Rome' }));
      setNextRefresh(120);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = () => {
    setLoading(true);
    fetchData();
    fetchRichieste();
    fetchCellaStats();
    toast.success('Dashboard aggiornata');
  };

  const handleOpenBinDetails = async (bin, docciaNum) => {
    try {
      const response = await axios.get(`${API}/bins/${bin.bin_number}/movimenti`);
      setBinMovimenti(response.data);
      setSelectedBin({ ...bin, doccia_number: docciaNum });
      setModalAction(null);
      // Inizializza form edit con dati correnti
      setEditData({
        lotto_number: bin.lotto || '',
        fornitore: bin.fornitore || '',
        prodotto: bin.prodotto || '',
        kg_attuali: bin.kg || 0
      });
      // Reset form uscita
      setUscitaData({
        kg_uscita: '',
        destinazione: '',
        data_ora_uscita: getItalianDateTime()
      });
      // Reset form spostamento
      setSpostamentoData({
        kg_uscita: bin.kg || 0,
        nuovo_bin_number: '',
        nuova_doccia_number: '',
        data_ora_uscita: getItalianDateTime()
      });
      // Reset form unione
      setUnioneData({
        bin_destinazione: '',
        kg_da_trasferire: bin.kg || 0,
        data_operazione: getItalianDateTime()
      });
      setBinsStessoLotto([]);
    } catch (error) {
      console.error('Error fetching bin movimenti:', error);
      setBinMovimenti([]);
      setSelectedBin({ ...bin, doccia_number: docciaNum });
    }
  };

  const closeBinDetails = () => {
    setSelectedBin(null);
    setBinMovimenti([]);
    setModalAction(null);
    setBinsStessoLotto([]);
  };

  // Carica bins stesso lotto per unione
  const loadBinsStessoLotto = async (binNumber) => {
    try {
      const response = await axios.get(`${API}/bins/${binNumber}/bins-stesso-lotto`);
      setBinsStessoLotto(response.data.bins_disponibili || []);
    } catch (error) {
      console.error('Error loading bins stesso lotto:', error);
      setBinsStessoLotto([]);
    }
  };

  // Handler per Modifica Bin
  const handleSubmitModifica = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await axios.put(`${API}/bins/${selectedBin.bin_number}`, editData);
      toast.success('Bin modificato con successo');
      fetchData();
      closeBinDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante modifica');
    } finally {
      setActionLoading(false);
    }
  };

  // Handler per Uscita Parziale
  const handleSubmitUscita = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const payload = {
        bin_number: selectedBin.bin_number,
        kg_uscita: parseFloat(uscitaData.kg_uscita),
        destinazione: uscitaData.destinazione,
        data_ora_uscita: new Date(uscitaData.data_ora_uscita).toISOString()
      };
      const response = await axios.post(`${API}/bins/uscita-parziale`, payload);
      toast.success(`Uscita registrata! Residuo: ${response.data.kg_residuo} kg`);
      fetchData();
      closeBinDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante uscita');
    } finally {
      setActionLoading(false);
    }
  };

  // Handler per Spostamento
  const handleSubmitSpostamento = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const payload = {
        bin_number: selectedBin.bin_number,
        kg_uscita: parseFloat(spostamentoData.kg_uscita),
        nuovo_bin_number: spostamentoData.nuovo_bin_number,
        nuova_doccia_number: parseInt(spostamentoData.nuova_doccia_number),
        data_ora_uscita: new Date(spostamentoData.data_ora_uscita).toISOString()
      };
      await axios.post(`${API}/bins/uscita-parziale`, payload);
      toast.success('Spostamento completato!');
      fetchData();
      closeBinDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante spostamento');
    } finally {
      setActionLoading(false);
    }
  };

  // Handler per Unione Bins
  const handleSubmitUnione = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const payload = {
        bin_origine: selectedBin.bin_number,
        bin_destinazione: unioneData.bin_destinazione,
        kg_da_trasferire: parseFloat(unioneData.kg_da_trasferire),
        data_operazione: new Date(unioneData.data_operazione).toISOString()
      };
      const response = await axios.post(`${API}/bins/unisci`, payload);
      toast.success(response.data.message);
      fetchData();
      closeBinDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante unione');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('it-IT', { timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return '-';
    }
  };

  const handleExportPDF = async () => {
    try {
      // Apri PDF in nuova finestra per garantire download
      window.open(`${API}/dashboard/export/pdf`, '_blank');
      toast.success('PDF in download...');
    } catch (error) {
      toast.error('Errore durante esportazione PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="loading-indicator">
        <div className="w-8 h-8 border-4 border-[#FFE500] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const kpiCards = [
    { label: 'Bins Usati Totali', value: stats?.bins_totali_usati || 0, icon: Box, gradient: 'from-blue-500 to-blue-600' },
    { label: 'KG in Depurazione', value: `${stats?.kg_in_depurazione || 0} kg`, icon: Scale, gradient: 'from-[#FFE500] to-amber-500' },
    { label: 'KG Usciti Oggi', value: `${stats?.kg_usciti_oggi || 0} kg`, icon: LogOut, gradient: 'from-emerald-500 to-emerald-600' },
  ];

  const docceStato = stats?.docce_stato || {};
  
  // Filtra doccia 6 (inutilizzabile)
  const docceFiltered = Object.entries(docceStato).filter(([num]) => num !== '6');

  // Componente Preview Verticale Doccia
  const DocciaPreview = ({ docciaNum, doccia }) => {
    const hasBins = doccia.numero_bins > 0;
    // Ordina bins per posizione (1=fondo)
    const sortedBins = [...(doccia.bins || [])].sort((a, b) => (a.posizione || 1) - (b.posizione || 1));
    
    return (
      <div
        className={`rounded-xl p-4 transition-all ${
          hasBins 
            ? 'bg-white/10 border-2 border-[#FFE500]/50 hover:border-[#FFE500]' 
            : 'bg-white/5 border border-white/10'
        }`}
        data-testid={`doccia-${docciaNum}`}
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-white uppercase">Doccia {docciaNum}</p>
          <span className={`text-xs font-bold text-white px-2 py-1 rounded-lg ${
            doccia.numero_bins >= 4 
              ? 'bg-red-500' 
              : doccia.numero_bins >= 3 
                ? 'bg-amber-500' 
                : hasBins 
                  ? 'bg-emerald-500' 
                  : 'bg-white/20'
          }`}>
            {doccia.numero_bins}/4
          </span>
        </div>
        
        {hasBins ? (
          <>
            <div className="text-xs text-white/50 mb-3">
              Totale: <span className="font-bold text-[#FFE500]">{doccia.kg_totali} kg</span>
            </div>
            
            {/* Preview Verticale - Bins impilati */}
            <div className="relative bg-gradient-to-b from-blue-900/30 to-blue-950/50 rounded-xl p-2 border border-blue-500/20">
              {/* Container bins */}
              <div className="flex flex-col gap-1">
                {sortedBins.map(bin => (
                  <div
                    key={bin.bin_number}
                    className="bg-gradient-to-r from-emerald-600/80 to-emerald-500/80 rounded-lg p-2 border border-emerald-400/30 shadow-lg cursor-pointer hover:from-emerald-500 hover:to-emerald-400 transition-all"
                    data-testid={`bin-preview-${docciaNum}-${bin.bin_number}`}
                    onClick={() => handleOpenBinDetails(bin, docciaNum)}
                  >
                    <div className="flex items-center justify-between">
                      {/* Info Bin */}
                      <div className="flex-1 text-center px-1">
                        <div className="text-xs font-bold text-white truncate">Bin {bin.bin_number}</div>
                        <div className="text-[10px] text-white/70 truncate">
                          {bin.kg}kg • L:{bin.lotto} {bin.id_fornitore ? `• ID:${bin.id_fornitore}` : ''}
                        </div>
                        {(bin.prodotto_sigla || bin.prodotto) && (
                          <div className="text-[9px] text-blue-200 truncate">
                            [{bin.prodotto_sigla || bin.prodotto}] {bin.fornitore?.slice(0, 10)}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Data ingresso */}
                    <div className="text-[9px] text-white/40 text-center mt-1">
                      {bin.data_inizio ? new Date(bin.data_inizio).toLocaleDateString('it-IT') : '-'}
                    </div>
                  </div>
                ))}
                {sortedBins.length === 0 && (
                  <div className="h-8 rounded-lg border border-dashed border-white/10 flex items-center justify-center">
                    <span className="text-[10px] text-white/20">Vuota</span>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white/5 rounded-xl p-4 border border-dashed border-white/10">
            <p className="text-xs text-white/30 text-center">Doccia vuota</p>
          </div>
        )}
        
        {/* Progress bar */}
        <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              doccia.numero_bins >= 4 
                ? 'bg-red-500' 
                : doccia.numero_bins >= 3 
                  ? 'bg-amber-500' 
                  : 'bg-emerald-500'
            }`}
            style={{ width: `${(doccia.numero_bins / 4) * 100}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white uppercase tracking-tight">Dashboard</h2>
            <p className="text-sm text-white/50 mt-1">
              {lastUpdate && `Aggiornato: ${lastUpdate}`}
              {' • '}
              <span className="text-[#FFE500]">Refresh: {Math.floor(nextRefresh / 60)}:{String(nextRefresh % 60).padStart(2, '0')}</span>
            </p>
          </div>
          
          {/* Badge Notifica Importazioni */}
          {richiesteCount > 0 && (
            <a 
              href="/da-importare"
              className={`flex items-center gap-2 px-3 py-2 bg-[#EF4444]/20 border border-[#EF4444] rounded-xl hover:bg-[#EF4444]/30 transition-all ${showNotification ? 'animate-pulse' : ''}`}
              data-testid="importazioni-badge"
            >
              <div className="relative">
                <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#EF4444] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {richiesteCount}
                </span>
              </div>
              <span className="text-sm font-bold text-[#EF4444] hidden sm:block">
                Da Importare
              </span>
            </a>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleManualRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white border border-white/20 rounded-xl font-medium uppercase tracking-wide text-xs hover:bg-white/20 transition-colors"
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Aggiorna
          </button>
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 px-4 py-2 bg-[#FFE500] text-[#0A1628] rounded-xl font-bold uppercase tracking-wide text-xs shadow-lg shadow-[#FFE500]/20 hover:bg-[#FFE500]/90 transition-colors"
            data-testid="export-docce-pdf-btn"
          >
            <FileDown className="w-4 h-4" />
            Stampa PDF
          </button>
        </div>
      </div>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kpi-section">
        {kpiCards.map((kpi, index) => {
          const Icon = kpi.icon;
          return (
            <div
              key={index}
              className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5 hover:bg-white/15 transition-colors"
              data-testid={`kpi-card-${index}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-white/50 uppercase tracking-wide font-medium">{kpi.label}</p>
                  <p className="text-3xl font-bold text-white mt-2">{kpi.value}</p>
                </div>
                <div className={`bg-gradient-to-br ${kpi.gradient} p-4 rounded-xl shadow-lg`}>
                  <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Stato Docce */}
      <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="docce-status-section">
        <div className="flex items-center gap-3 mb-5">
          <div className="bg-[#FFE500] p-2 rounded-xl">
            <Droplets className="w-5 h-5 text-[#0A1628]" />
          </div>
          <h3 className="text-lg font-bold text-white uppercase tracking-tight">Stato Docce (esclusa D6)</h3>
          <span className="text-xs text-white/40 ml-auto">Clicca su un bin per dettagli</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {docceFiltered.map(([docciaNum, doccia]) => (
            <DocciaPreview key={docciaNum} docciaNum={docciaNum} doccia={doccia} />
          ))}
        </div>
      </div>

      {/* Totali per Lotto */}
      {stats?.totali_per_lotto && stats.totali_per_lotto.length > 0 && (
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="totali-lotto-section">
          <div className="flex items-center gap-3 mb-5">
            <div className="bg-emerald-500 p-2 rounded-xl">
              <Layers className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-bold text-white uppercase tracking-tight">Totali KG per Lotto</h3>
            <span className="text-xs text-white/40 ml-auto">
              {stats.totali_per_lotto.length} {stats.totali_per_lotto.length === 1 ? 'lotto' : 'lotti'} in depurazione
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.totali_per_lotto.map((lotto, index) => (
              <div 
                key={lotto.lotto}
                className="bg-gradient-to-br from-white/10 to-white/5 border border-white/20 rounded-xl p-4 hover:border-emerald-500/50 transition-all"
                data-testid={`lotto-card-${index}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-xs text-white/50 uppercase tracking-wide">Lotto</p>
                    <p className="text-2xl font-bold text-white">{lotto.lotto}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-emerald-400">{lotto.kg_totali}</p>
                    <p className="text-xs text-emerald-400/70 uppercase">kg totali</p>
                  </div>
                </div>
                
                <div className="space-y-2 pt-3 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/50">Fornitore</span>
                    <span className="text-sm font-medium text-white">{lotto.fornitore}</span>
                  </div>
                  {(lotto.prodotto_sigla || lotto.prodotto) && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/50">Prodotto</span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-lg font-bold">
                        <Package className="w-3 h-3" />
                        {lotto.prodotto_sigla || lotto.prodotto}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/50">Bins</span>
                    <span className="text-sm font-medium text-[#FFE500]">{lotto.num_bins}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/50">Docce</span>
                    <div className="flex gap-1">
                      {lotto.docce.map(d => (
                        <span key={d} className="px-2 py-0.5 bg-white/10 text-white text-xs rounded font-medium">
                          {d}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Totale complessivo */}
          <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
            <span className="text-sm text-white/70">Totale complessivo in depurazione</span>
            <span className="text-2xl font-bold text-[#FFE500]">
              {stats.totali_per_lotto.reduce((sum, l) => sum + l.kg_totali, 0).toFixed(2)} kg
            </span>
          </div>
        </div>
      )}

      {/* Totali per ID Fornitore */}
      {stats?.totali_per_id && stats.totali_per_id.length > 0 && (
        <div className="bg-cyan-500/10 backdrop-blur border border-cyan-500/30 rounded-2xl p-6" data-testid="totali-id-section">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-cyan-500/20 rounded-lg">
              <Hash className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-bold text-white uppercase tracking-tight">Totali per ID</h3>
              <p className="text-xs text-white/50">
                {stats.totali_per_id.length} ID con merce attiva - Clicca per dettagli
              </p>
            </div>
          </div>
          
          <div className="space-y-2">
            {stats.totali_per_id.map((item, index) => (
              <div key={index} data-testid={`totale-id-${index}`}>
                <div 
                  onClick={() => setExpandedId(expandedId === item.id_fornitore ? null : item.id_fornitore)}
                  className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    {expandedId === item.id_fornitore ? (
                      <ChevronDown className="w-4 h-4 text-cyan-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-white/50" />
                    )}
                    <span className="px-3 py-1.5 bg-cyan-500/30 text-cyan-400 rounded-lg font-mono font-bold text-lg">
                      {item.id_fornitore}
                    </span>
                    <div>
                      <p className="text-white text-sm">
                        {item.num_bins} {item.num_bins === 1 ? 'bin' : 'bins'}
                      </p>
                      <p className="text-xs text-white/50">
                        Lotti: {item.lotti.join(', ')}
                      </p>
                    </div>
                  </div>
                  <span className="font-bold text-cyan-400 text-lg">{item.kg_totali} kg</span>
                </div>
                
                {/* Dettagli bins espansi */}
                {expandedId === item.id_fornitore && item.bins && (
                  <div className="ml-8 mt-2 space-y-1 border-l-2 border-cyan-500/30 pl-4">
                    {item.bins.map((bin, binIdx) => (
                      <div 
                        key={binIdx}
                        className="flex items-center justify-between p-2 bg-white/5 rounded-lg text-sm"
                      >
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded font-bold">
                            Bin {bin.bin_number}
                          </span>
                          <span className="text-white/50">Doccia {bin.doccia}</span>
                          <span className="text-white/50">L: {bin.lotto}</span>
                          {bin.prodotto && (
                            <span className="text-blue-400">[{bin.prodotto}]</span>
                          )}
                          {bin.data_inizio && (
                            <span className="text-yellow-400/70 text-xs">
                              Entrata: {new Date(bin.data_inizio).toLocaleDateString('it-IT')} {new Date(bin.data_inizio).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}
                            </span>
                          )}
                        </div>
                        <span className="font-bold text-emerald-400">{bin.kg} kg</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Totale complessivo per ID */}
          <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center">
            <span className="text-sm text-white/70">Totale per tutti gli ID</span>
            <span className="text-2xl font-bold text-cyan-400">
              {stats.totali_per_id.reduce((sum, i) => sum + i.kg_totali, 0).toFixed(2)} kg
            </span>
          </div>
        </div>
      )}

      {/* ============ SEZIONE CELLA (NO DEPURAZIONE) ============ */}
      {cellaStats.bins_attivi > 0 && (
        <div className="bg-[#8B5CF6]/10 backdrop-blur border border-[#8B5CF6]/30 rounded-2xl p-6" data-testid="cella-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-[#8B5CF6]/20 rounded-lg">
                <Package className="w-5 h-5 text-[#8B5CF6]" />
              </div>
              <div>
                <h3 className="font-bold text-white">Giacenza Cella</h3>
                <p className="text-xs text-white/50">Prodotti senza depurazione</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#8B5CF6]">{cellaStats.kg_totale.toFixed(2)} kg</p>
              <p className="text-xs text-white/50">{cellaStats.bins_attivi} prodotti</p>
            </div>
          </div>
          
          {/* Lista prodotti in cella */}
          <div className="space-y-2">
            {cellaStats.bins.map((bin, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/10">
                <div className="flex items-center gap-3">
                  <span className="px-2 py-1 bg-[#8B5CF6]/20 text-[#8B5CF6] rounded font-mono font-bold text-sm">
                    {bin.prodotto_sigla}
                  </span>
                  <div>
                    <p className="text-white font-medium text-sm">{bin.prodotto_nome}</p>
                    <p className="text-xs text-white/50">{bin.fornitore}</p>
                  </div>
                </div>
                <span className="font-bold text-[#22C55E]">{bin.kg_attuali} kg</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal Dettagli Bin */}
      {selectedBin && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={closeBinDetails}>
          <div 
            className="bg-[#0F172A] border border-white/20 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 p-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">Bin {selectedBin.bin_number}</h3>
                <p className="text-sm text-white/80">Doccia {selectedBin.doccia_number}</p>
              </div>
              <button 
                onClick={closeBinDetails}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Info Principali */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                  <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                    <Calendar className="w-3 h-3" />
                    Data Ingresso
                  </div>
                  <p className="text-white font-bold">{formatDate(selectedBin.data_inizio)}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                  <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                    <Scale className="w-3 h-3" />
                    KG Iniziali
                  </div>
                  <p className="text-white font-bold">{selectedBin.kg_iniziali || selectedBin.kg} kg</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                  <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                    <Scale className="w-3 h-3" />
                    KG Attuali
                  </div>
                  <p className="text-emerald-400 font-bold text-lg">{selectedBin.kg} kg</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                  <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                    <Truck className="w-3 h-3" />
                    Fornitore
                  </div>
                  <p className="text-white font-bold text-sm truncate">{selectedBin.fornitore || '-'}</p>
                </div>
              </div>
              
              {/* Lotto e Prodotto */}
              <div className="flex gap-3">
                <div className="flex-1 bg-white/5 rounded-xl p-3 border border-white/10">
                  <p className="text-white/50 text-xs mb-1">Lotto</p>
                  <p className="text-[#FFE500] font-bold">{selectedBin.lotto || '-'}</p>
                </div>
                <div className="flex-1 bg-white/5 rounded-xl p-3 border border-white/10">
                  <p className="text-white/50 text-xs mb-1">ID</p>
                  <p className="text-cyan-400 font-bold">{selectedBin.id_fornitore || '-'}</p>
                </div>
                <div className="flex-1 bg-white/5 rounded-xl p-3 border border-white/10">
                  <p className="text-white/50 text-xs mb-1">Prodotto</p>
                  <p className="text-blue-400 font-bold">[{selectedBin.prodotto_sigla || selectedBin.prodotto || '-'}]</p>
                </div>
              </div>
              
              {/* Movimenti */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <ArrowRightLeft className="w-4 h-4 text-white/50" />
                  <h4 className="text-white font-bold">Movimenti ({binMovimenti.length})</h4>
                </div>
                
                {binMovimenti.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {binMovimenti.map((mov, idx) => (
                      <div key={idx} className="bg-white/5 rounded-lg p-2 border border-white/10 text-sm">
                        <div className="flex items-center justify-between">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            mov.tipo_movimento === 'ingresso' ? 'bg-emerald-500/20 text-emerald-400' :
                            mov.tipo_movimento === 'uscita_parziale' ? 'bg-amber-500/20 text-amber-400' :
                            mov.tipo_movimento === 'uscita_totale' ? 'bg-red-500/20 text-red-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {mov.tipo_movimento?.replace('_', ' ').toUpperCase()}
                          </span>
                          <span className="text-white/50 text-xs">{formatDate(mov.timestamp || mov.data_ora_uscita)}</span>
                        </div>
                        {mov.kg_movimento && (
                          <p className="text-white/70 text-xs mt-1">{mov.kg_movimento} kg</p>
                        )}
                        {mov.destinazione && (
                          <p className="text-white/50 text-xs">→ {mov.destinazione}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/30 text-sm text-center py-4">Nessun movimento registrato</p>
                )}
              </div>
              
              {/* Pulsanti Azioni */}
              {!modalAction && (
                <div className="flex gap-2 pt-3 border-t border-white/10">
                  <button
                    onClick={() => setModalAction('modifica')}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-xl text-sm font-bold hover:bg-blue-500/30 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    Modifica
                  </button>
                  <button
                    onClick={() => setModalAction('uscita')}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-xl text-sm font-bold hover:bg-amber-500/30 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Uscita
                  </button>
                  <button
                    onClick={() => setModalAction('spostamento')}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-xl text-sm font-bold hover:bg-purple-500/30 transition-colors"
                  >
                    <MoveRight className="w-4 h-4" />
                    Sposta
                  </button>
                  <button
                    onClick={() => {
                      setModalAction('unione');
                      loadBinsStessoLotto(selectedBin.bin_number);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 rounded-xl text-sm font-bold hover:bg-cyan-500/30 transition-colors"
                  >
                    <MoveRight className="w-4 h-4" />
                    Unisci
                  </button>
                </div>
              )}
              
              {/* Form Modifica */}
              {modalAction === 'modifica' && (
                <form onSubmit={handleSubmitModifica} className="space-y-3 pt-3 border-t border-white/10">
                  <h4 className="text-white font-bold flex items-center gap-2">
                    <Edit className="w-4 h-4 text-blue-400" />
                    Modifica Bin
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-white/50 text-xs">Lotto</label>
                      <input
                        type="text"
                        value={editData.lotto_number}
                        onChange={(e) => setEditData({...editData, lotto_number: e.target.value})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs">KG Attuali</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editData.kg_attuali}
                        onChange={(e) => setEditData({...editData, kg_attuali: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs">Fornitore</label>
                      <select
                        value={editData.fornitore}
                        onChange={(e) => setEditData({...editData, fornitore: e.target.value})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                      >
                        <option value="">Seleziona...</option>
                        {fornitori.map(f => (
                          <option key={f.nome} value={f.nome}>{f.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-white/50 text-xs">Prodotto</label>
                      <select
                        value={editData.prodotto}
                        onChange={(e) => setEditData({...editData, prodotto: e.target.value})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                      >
                        <option value="">Seleziona...</option>
                        {prodotti.map(p => (
                          <option key={p.nome} value={p.nome}>{p.nome} [{p.sigla}]</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setModalAction(null)}
                      className="flex-1 px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg text-sm font-bold hover:bg-white/20"
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-bold hover:bg-blue-600 disabled:opacity-50"
                    >
                      {actionLoading ? 'Salvataggio...' : <><Save className="w-4 h-4" /> Salva</>}
                    </button>
                  </div>
                </form>
              )}
              
              {/* Form Uscita */}
              {modalAction === 'uscita' && (
                <form onSubmit={handleSubmitUscita} className="space-y-3 pt-3 border-t border-white/10">
                  <h4 className="text-white font-bold flex items-center gap-2">
                    <LogOut className="w-4 h-4 text-amber-400" />
                    Registra Uscita (disponibili: {selectedBin.kg} kg)
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-white/50 text-xs">KG Uscita *</label>
                      <input
                        type="number"
                        step="0.1"
                        max={selectedBin.kg}
                        required
                        value={uscitaData.kg_uscita}
                        onChange={(e) => setUscitaData({...uscitaData, kg_uscita: e.target.value})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                        placeholder="Es: 10"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs">Data/Ora</label>
                      <input
                        type="datetime-local"
                        value={uscitaData.data_ora_uscita}
                        onChange={(e) => setUscitaData({...uscitaData, data_ora_uscita: e.target.value})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-white/50 text-xs">Destinazione</label>
                      <input
                        type="text"
                        value={uscitaData.destinazione}
                        onChange={(e) => setUscitaData({...uscitaData, destinazione: e.target.value})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                        placeholder="Es: Cliente XYZ"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setModalAction(null)}
                      className="flex-1 px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg text-sm font-bold hover:bg-white/20"
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      disabled={actionLoading || !uscitaData.kg_uscita}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-bold hover:bg-amber-600 disabled:opacity-50"
                    >
                      {actionLoading ? 'Registrazione...' : <><LogOut className="w-4 h-4" /> Conferma Uscita</>}
                    </button>
                  </div>
                </form>
              )}
              
              {/* Form Spostamento */}
              {modalAction === 'spostamento' && (
                <form onSubmit={handleSubmitSpostamento} className="space-y-3 pt-3 border-t border-white/10">
                  <h4 className="text-white font-bold flex items-center gap-2">
                    <MoveRight className="w-4 h-4 text-purple-400" />
                    Sposta Bin in altra Doccia
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-white/50 text-xs">Nuovo Numero Bin *</label>
                      <input
                        type="text"
                        required
                        value={spostamentoData.nuovo_bin_number}
                        onChange={(e) => setSpostamentoData({...spostamentoData, nuovo_bin_number: e.target.value})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                        placeholder="Es: 15"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs">Nuova Doccia *</label>
                      <select
                        required
                        value={spostamentoData.nuova_doccia_number}
                        onChange={(e) => setSpostamentoData({...spostamentoData, nuova_doccia_number: e.target.value})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                      >
                        <option value="">Seleziona...</option>
                        {[1,2,3,4,5,7,8,9,10,11,12].map(d => (
                          <option key={d} value={d}>Doccia {d}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-white/50 text-xs">KG da spostare</label>
                      <input
                        type="number"
                        step="0.1"
                        max={selectedBin.kg}
                        value={spostamentoData.kg_uscita}
                        onChange={(e) => setSpostamentoData({...spostamentoData, kg_uscita: e.target.value})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs">Data/Ora</label>
                      <input
                        type="datetime-local"
                        value={spostamentoData.data_ora_uscita}
                        onChange={(e) => setSpostamentoData({...spostamentoData, data_ora_uscita: e.target.value})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setModalAction(null)}
                      className="flex-1 px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg text-sm font-bold hover:bg-white/20"
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      disabled={actionLoading || !spostamentoData.nuovo_bin_number || !spostamentoData.nuova_doccia_number}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-purple-500 text-white rounded-lg text-sm font-bold hover:bg-purple-600 disabled:opacity-50"
                    >
                      {actionLoading ? 'Spostamento...' : <><MoveRight className="w-4 h-4" /> Conferma Spostamento</>}
                    </button>
                  </div>
                </form>
              )}

              {/* Form Unione */}
              {modalAction === 'unione' && (
                <form onSubmit={handleSubmitUnione} className="space-y-3 pt-3 border-t border-white/10">
                  <h4 className="text-white font-bold flex items-center gap-2">
                    <MoveRight className="w-4 h-4 text-cyan-400" />
                    Unisci in altro Bin (stesso lotto)
                  </h4>
                  <p className="text-xs text-white/50">
                    Trasferisci kg da questo bin in un altro bin esistente con lo stesso lotto ({selectedBin?.lotto})
                  </p>
                  
                  {binsStessoLotto.length === 0 ? (
                    <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <p className="text-amber-400 text-sm text-center">
                        Nessun altro bin attivo con lo stesso lotto trovato.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="text-white/50 text-xs">Bin Destinazione *</label>
                        <select
                          value={unioneData.bin_destinazione}
                          onChange={(e) => setUnioneData(prev => ({...prev, bin_destinazione: e.target.value}))}
                          className="w-full px-3 py-2 bg-[#0A1628] border border-white/20 rounded-lg text-white text-sm"
                          required
                        >
                          <option value="">Seleziona bin...</option>
                          {binsStessoLotto.map(b => (
                            <option key={b.bin_number} value={b.bin_number}>
                              {b.bin_number} - Doccia {b.doccia_number} - {b.kg_attuali?.toFixed(1)} kg
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-white/50 text-xs">KG da Trasferire *</label>
                          <input
                            type="number"
                            step="0.1"
                            value={unioneData.kg_da_trasferire}
                            onChange={(e) => setUnioneData(prev => ({...prev, kg_da_trasferire: e.target.value}))}
                            className="w-full px-3 py-2 bg-[#0A1628] border border-white/20 rounded-lg text-white text-sm"
                            max={selectedBin?.kg}
                            required
                          />
                          <p className="text-xs text-white/30 mt-1">Max: {selectedBin?.kg} kg</p>
                        </div>
                        <div>
                          <label className="text-white/50 text-xs">Data/Ora Operazione</label>
                          <input
                            type="datetime-local"
                            value={unioneData.data_operazione}
                            onChange={(e) => setUnioneData(prev => ({...prev, data_operazione: e.target.value}))}
                            className="w-full px-3 py-2 bg-[#0A1628] border border-white/20 rounded-lg text-white text-sm"
                            required
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setModalAction(null)}
                          className="flex-1 px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg text-sm font-bold hover:bg-white/20"
                        >
                          Annulla
                        </button>
                        <button
                          type="submit"
                          disabled={actionLoading || !unioneData.bin_destinazione || !unioneData.kg_da_trasferire}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-cyan-500 text-white rounded-lg text-sm font-bold hover:bg-cyan-600 disabled:opacity-50"
                        >
                          {actionLoading ? 'Trasferimento...' : <><MoveRight className="w-4 h-4" /> Conferma Unione</>}
                        </button>
                      </div>
                    </>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
